import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  ProposalResponse,
  SuggestionResponse,
  WorklistResponse,
  WorklistResolution,
  WorklistSummaryResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { useAuthStore } from '../auth/authStore';
import { useLocalisedName } from '../i18n/localisedName';
import { clinicDay, clinicTime, longDate } from './scheduling/clinicTime';
import { useBranchPicker } from './scheduling/useBranchPicker';

/**
 * The desk's queue of stranded appointments, and what to do about each one (FR-306, DEC-031).
 *
 * A doctor blocks a day and nine patients are left with nobody to see them. This is the screen that
 * turns that into nine decisions somebody has actually made, rather than nine cancellations the
 * software made on the clinic's behalf.
 *
 * The reassignment path is the interesting one and it takes three steps on purpose: the desk picks
 * from ranked suggestions, the replacement doctor agrees, and only then is the patient asked
 * (RULE-011). Each step is visible here, because the receptionist fielding the telephone call is
 * the person who needs to know which of the two is still outstanding.
 */
export function WorklistsPage() {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const permissions = useAuthStore((state) => state.permissions);
  const canResolve = permissions.has('WORKLIST_RESOLVE');

  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const [openId, setOpenId] = useState<string | null>(null);

  const worklists = useApiQuery<WorklistSummaryResponse[]>(
    queryKeys.worklists(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/worklists?branchId=${branchId}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.worklists')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.worklists')} description={t('worklists.description')} />

      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('board.branch')}</span>
          <select
            className="field__input"
            value={branchId ?? ''}
            onChange={(event) => {
              choose(event.target.value);
              setOpenId(null);
            }}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {localised.name(branch)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <AsyncSection
        query={worklists}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('worklists.emptyTitle')}
        emptyDescription={t('worklists.emptyDescription')}
      >
        {(lists) => (
          <div className="stack">
            {lists.map((list) => (
              <article className="panel" key={list.id}>
                <header className="panel__header">
                  <div>
                    <h2>{i18n.language.startsWith('ar') ? list.titleAr : list.titleEn}</h2>
                    <p className="panel__meta">
                      {t('worklists.opened', { date: longDate(clinicDay(list.openedAt), i18n.language) })}
                      {' · '}
                      {t('worklists.pending', {
                        pending: list.pendingItems ?? 0,
                        total: list.totalItems ?? 0,
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button button--ghost button--compact"
                    onClick={() => setOpenId(openId === list.id ? null : (list.id ?? null))}
                  >
                    {openId === list.id ? t('common.close') : t('worklists.open')}
                  </button>
                </header>

                {openId === list.id ? (
                  <WorklistDetail
                    clinicId={clinicId}
                    worklistId={list.id ?? ''}
                    canResolve={canResolve}
                  />
                ) : null}
              </article>
            ))}
          </div>
        )}
      </AsyncSection>
    </>
  );
}

interface DetailProps {
  readonly clinicId: string;
  readonly worklistId: string;
  readonly canResolve: boolean;
}

function WorklistDetail({ clinicId, worklistId, canResolve }: DetailProps) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const [reassigning, setReassigning] = useState<string | null>(null);

  const detail = useApiQuery<WorklistResponse>(
    queryKeys.worklist(clinicId, worklistId),
    `/clinics/${clinicId}/worklists/${worklistId}`,
  );

  const listKey = ['clinics', clinicId, 'worklists'] as const;

  const resolve = useApiMutation<
    WorklistResponse,
    { readonly itemId: string; readonly resolution: WorklistResolution; readonly note: string }
  >(
    ({ itemId, resolution, note }) => ({
      path: `/clinics/${clinicId}/worklists/items/${itemId}/resolve`,
      options: { method: 'POST', body: { resolution, note } },
    }),
    { invalidates: [listKey] },
  );

  return (
    <AsyncSection query={detail} isEmpty={(data) => (data.items ?? []).length === 0}>
      {(data) => (
        <ul className="list">
          {(data.items ?? []).map((item) => {
            const appointment = item.appointment;
            const pending = item.status === 'PENDING';
            return (
              <li className="list__row" key={item.id}>
                <div className="list__main">
                  <strong>{appointment?.patientName}</strong>
                  <span className="list__meta">
                    {clinicTime(appointment?.startsAt)}
                    {' · '}
                    {localised.name({
                      nameEn: appointment?.serviceNameEn,
                      nameAr: appointment?.serviceNameAr,
                    })}
                    {appointment?.patientPhone ? ` · ${appointment.patientPhone}` : ''}
                  </span>
                  {!pending ? (
                    <span className="badge badge--success">
                      {t(`worklists.resolution.${item.resolution}`)}
                      {item.resolutionNote ? ` — ${item.resolutionNote}` : ''}
                    </span>
                  ) : null}
                </div>

                {pending && canResolve ? (
                  <div className="list__actions">
                    {/* A branch closure has already cancelled its bookings (CR-12): there is no
                        appointment left to hand to another doctor, only a patient to call. */}
                    {appointment?.status !== 'CANCELLED' ? (
                      <button
                        type="button"
                        className="button button--primary button--compact"
                        onClick={() =>
                          setReassigning(reassigning === item.id ? null : (item.id ?? null))
                        }
                      >
                        {t('worklists.reassign')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="button button--ghost button--compact"
                      onClick={() =>
                        resolve.mutate({
                          itemId: item.id ?? '',
                          resolution: 'CONTACTED',
                          note:
                            appointment?.status === 'CANCELLED'
                              ? t('worklists.rebookedNote')
                              : t('worklists.contactedNote'),
                        })
                      }
                    >
                      {t('worklists.resolution.CONTACTED')}
                    </button>
                    <button
                      type="button"
                      className="button button--ghost button--compact"
                      onClick={() =>
                        resolve.mutate({
                          itemId: item.id ?? '',
                          resolution: 'NO_ACTION_NEEDED',
                          note: '',
                        })
                      }
                    >
                      {t('worklists.resolution.NO_ACTION_NEEDED')}
                    </button>
                  </div>
                ) : null}

                {reassigning === item.id && appointment?.id ? (
                  <ReassignmentPanel
                    clinicId={clinicId}
                    appointmentId={appointment.id}
                    worklistItemId={item.id ?? ''}
                    onDone={() => setReassigning(null)}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </AsyncSection>
  );
}

interface ReassignProps {
  readonly clinicId: string;
  readonly appointmentId: string;
  readonly worklistItemId: string;
  readonly onDone: () => void;
}

/**
 * Ranked suggestions, and the two answers a reassignment needs (DEC-031, RULE-011).
 *
 * The fairness number is shown rather than hidden. The desk may disagree with the ranking — they
 * know things it does not — but they should have to do so knowingly, and "Dr Nour: 6h booked this
 * week" is what makes that possible.
 */
function ReassignmentPanel({ clinicId, appointmentId, worklistItemId, onDone }: ReassignProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');

  const suggestions = useApiQuery<SuggestionResponse[]>(
    queryKeys.reassignmentSuggestions(clinicId, appointmentId),
    `/clinics/${clinicId}/reassignments/suggestions?appointmentId=${appointmentId}`,
  );

  const proposals = useApiQuery<ProposalResponse[]>(
    queryKeys.proposals(clinicId, appointmentId),
    `/clinics/${clinicId}/reassignments/appointments/${appointmentId}`,
  );

  const invalidates = [
    ['clinics', clinicId, 'worklists'] as const,
    ['clinics', clinicId, 'reassignments', appointmentId] as const,
  ];

  const propose = useApiMutation<ProposalResponse, { readonly toEmploymentId: string; readonly rank: number }>(
    ({ toEmploymentId, rank }) => ({
      path: `/clinics/${clinicId}/reassignments/appointments/${appointmentId}`,
      options: {
        method: 'POST',
        body: { toEmploymentId, worklistItemId, note, suggestionRank: rank },
      },
    }),
    { invalidates },
  );

  const decide = useApiMutation<
    ProposalResponse,
    { readonly proposalId: string; readonly who: 'doctor' | 'patient'; readonly accepted: boolean }
  >(
    ({ proposalId, who, accepted }) => ({
      path: `/clinics/${clinicId}/reassignments/${proposalId}/${who}-decision`,
      options: {
        method: 'POST',
        body: {
          accepted,
          reason: accepted ? undefined : t('worklists.declinedByPhone'),
          onBehalf: true,
        },
      },
    }),
    { invalidates, onSuccess: () => onDone() },
  );

  const live = (proposals.data ?? []).find(
    (proposal) => proposal.status === 'PROPOSED' || proposal.status === 'AWAITING_PATIENT',
  );

  if (live) {
    return (
      <div className="panel panel--nested">
        <p className="panel__meta">
          {t(`worklists.proposalStatus.${live.status}`, { doctor: live.toDoctorName ?? '' })}
        </p>
        <div className="list__actions">
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() =>
              decide.mutate({
                proposalId: live.id ?? '',
                who: live.status === 'PROPOSED' ? 'doctor' : 'patient',
                accepted: true,
              })
            }
          >
            {live.status === 'PROPOSED'
              ? t('worklists.recordDoctorYes')
              : t('worklists.recordPatientYes')}
          </button>
          <button
            type="button"
            className="button button--ghost button--compact"
            onClick={() =>
              decide.mutate({
                proposalId: live.id ?? '',
                who: live.status === 'PROPOSED' ? 'doctor' : 'patient',
                accepted: false,
              })
            }
          >
            {t('worklists.recordNo')}
          </button>
        </div>
        <p className="hint">{t('worklists.consentHint')}</p>
      </div>
    );
  }

  return (
    <div className="panel panel--nested">
      <label className="field">
        <span className="field__label">{t('worklists.noteLabel')}</span>
        <input
          className="field__input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('worklists.notePlaceholder')}
        />
      </label>

      <AsyncSection
        query={suggestions}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('worklists.noSuggestionsTitle')}
        emptyDescription={t('worklists.noSuggestionsDescription')}
      >
        {(ranked) => (
          <ul className="list">
            {ranked.map((suggestion) => (
              <li className="list__row" key={suggestion.employmentId}>
                <div className="list__main">
                  <strong>{suggestion.doctorName}</strong>
                  <span className="list__meta">
                    {t('worklists.load', {
                      hours: Math.round(((suggestion.bookedMinutesThisWeek ?? 0) / 60) * 10) / 10,
                    })}
                  </span>
                </div>
                <div className="list__actions">
                  <button
                    type="button"
                    className="button button--compact"
                    disabled={propose.isPending}
                    onClick={() =>
                      propose.mutate({
                        toEmploymentId: suggestion.employmentId ?? '',
                        rank: suggestion.rank ?? 0,
                      })
                    }
                  >
                    {t('worklists.ask', { doctor: suggestion.doctorName ?? '' })}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncSection>

      <p className="hint">{t('worklists.rankingHint')}</p>
    </div>
  );
}
